import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

import { API_URL } from '../lib/apiUrl';

const TeamContext = createContext(null);

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) throw new Error('useTeam must be used within TeamProvider');
  return context;
};

export const TeamProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentTeam?.id) {
      localStorage.setItem('selectedTeamId', currentTeam.id);
    }
  }, [currentTeam]);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/teams`);
      setTeams(response.data);
      if (response.data.length > 0) {
        const savedTeamId = localStorage.getItem('selectedTeamId');
        const savedTeam = savedTeamId ? response.data.find(t => t.id === savedTeamId) : null;
        if (!currentTeam || !response.data.find(t => t.id === currentTeam.id)) {
          setCurrentTeam(savedTeam || response.data[0]);
        }
      } else {
        setCurrentTeam(null);
        localStorage.removeItem('selectedTeamId');
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [currentTeam]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTeams();
    } else {
      setTeams([]);
      setCurrentTeam(null);
      setInitialized(true);
      localStorage.removeItem('selectedTeamId');
    }
  }, [isAuthenticated, fetchTeams]);

  const createTeam = async (teamData) => {
    const response = await axios.post(`${API_URL}/api/teams`, teamData);
    const newTeam = response.data;
    setTeams(prev => [...prev, newTeam]);
    if (!currentTeam) setCurrentTeam(newTeam);
    return newTeam;
  };

  const updateTeam = async (teamId, teamData) => {
    const response = await axios.put(`${API_URL}/api/teams/${teamId}`, teamData);
    setTeams(prev => prev.map(t => t.id === teamId ? response.data : t));
    if (currentTeam?.id === teamId) setCurrentTeam(response.data);
    return response.data;
  };

  const deleteTeam = async (teamId) => {
    await axios.delete(`${API_URL}/api/teams/${teamId}`);
    setTeams(prev => prev.filter(t => t.id !== teamId));
    if (currentTeam?.id === teamId) {
      const remaining = teams.filter(t => t.id !== teamId);
      setCurrentTeam(remaining[0] || null);
    }
  };

  const selectTeam = (team) => setCurrentTeam(team);

  return (
    <TeamContext.Provider value={{
      teams, currentTeam, loading, initialized,
      fetchTeams, createTeam, updateTeam, deleteTeam, selectTeam
    }}>
      {children}
    </TeamContext.Provider>
  );
};
